/** D-126: invoice or quote typed by hand — from Billing or from the client's sheet. Lines from the
 * catalogue or free (label, quantity, unit price in FCFA), discount, due date, schedule, notes;
 * saved as a draft (edited later) or issued at once (number of the agency, PDF with the layout). */

import {
  type InvoiceDetail,
  type ManualInvoiceKindEnum,
  useBillingInvoicesCreate,
  useBillingInvoicesPartialUpdate,
  useBillingSettingsList,
  useCatalogPackagesList,
  useCrmContactsList,
} from "@agencyos/api-client";
import { formatXaf, invoiceTotals, splitInstalments } from "@agencyos/core";
import { Button, GlassSheet, SelectField, SoftToggle, TextAreaField, TextField } from "@agencyos/ui-web";
import { Plus, Trash2 } from "lucide-react";
import { type FormEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { localized } from "../../components/TranslatedFields";
import { fieldErrors } from "../../lib/settings";
import { errorText } from "../contracts/shared";
import { amountOf, todayInDouala, useRefreshBilling } from "./shared";

interface Line {
  key: number;
  package: string;
  label: string;
  quantity: string;
  unit: string;
}

interface Instalment {
  key: number;
  due: string;
  amount: string;
}

let nextKey = 1;
const newLine = (): Line => ({ key: nextKey++, package: "", label: "", quantity: "1", unit: "" });

function labelText(label: unknown, language: string): string {
  return typeof label === "string" ? label : localized(label, language);
}

function linesOf(invoice: InvoiceDetail | undefined, language: string): Line[] {
  if (!invoice?.lines.length) return [newLine()];
  return invoice.lines.map((line) => ({
    key: nextKey++,
    package: line.package ?? "",
    label: labelText(line.label, language),
    quantity: String(line.quantity ?? 1),
    unit: String(line.unit_price_xaf ?? line.amount_xaf ?? ""),
  }));
}

export function InvoiceFormSheet({
  open,
  onClose,
  contactId,
  contactName,
  draft,
}: {
  open: boolean;
  onClose: () => void;
  /** Fixed client (from the client's sheet). */
  contactId?: string;
  contactName?: string;
  /** A draft to change. */
  draft?: InvoiceDetail;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const refresh = useRefreshBilling();
  const create = useBillingInvoicesCreate();
  const update = useBillingInvoicesPartialUpdate();
  const mutation = draft ? update : create;
  const [search, setSearch] = useState("");
  const [contact, setContact] = useState(draft?.contact ?? contactId ?? "");
  const [kind, setKind] = useState<ManualInvoiceKindEnum>(draft?.kind === "quote" ? "quote" : "invoice");
  const [language, setLanguage] = useState<"" | "fr" | "en">(draft?.language === "en" ? "en" : draft ? "fr" : "");
  const [lines, setLines] = useState<Line[]>(() => linesOf(draft, i18n.language));
  const [discount, setDiscount] = useState(draft?.discount_xaf ? String(draft.discount_xaf) : "");
  const [due, setDue] = useState(draft?.due_date ?? "");
  const [notes, setNotes] = useState(draft?.notes ?? "");
  const [withSchedule, setWithSchedule] = useState(Boolean(draft?.installments.length));
  const [schedule, setSchedule] = useState<Instalment[]>(
    () => draft?.installments.map((i) => ({ key: nextKey++, due: i.due_date, amount: String(i.amount_xaf) })) ?? [],
  );
  const [localError, setLocalError] = useState("");
  const fixed = Boolean(contactId || draft);
  const contacts = useCrmContactsList(search.trim() ? { q: search.trim() } : undefined, { query: { enabled: open && !fixed } });
  const packages = useCatalogPackagesList(undefined, { query: { enabled: open } });
  const settings = useBillingSettingsList(undefined, { query: { enabled: open } });
  const found = contacts.data?.status === 200 ? contacts.data.data.results : [];
  const published = (packages.data?.status === 200 ? packages.data.data.results : []).filter((p) => p.status === "published");
  const vat = settings.data?.status === 200 ? settings.data.data.results[0] : undefined;
  const errors = fieldErrors(mutation.error);

  const totals = useMemo(
    () =>
      invoiceTotals(
        lines.map((line) => ({ quantity: amountOf(line.quantity) || 0, unitPriceXaf: amountOf(line.unit) || 0 })),
        amountOf(discount) || 0,
        vat ? { vatEnabled: vat.vat_enabled ?? true, vatRateBp: vat.vat_rate_bp ?? 0, pricesIncludeVat: vat.prices_include_vat ?? true } : undefined,
      ),
    [lines, discount, vat],
  );
  const scheduled = schedule.reduce((sum, row) => sum + (amountOf(row.amount) || 0), 0);

  const setLine = (key: number, patch: Partial<Line>) => setLines((all) => all.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  const pickPackage = (key: number, id: string) => {
    const pack = published.find((p) => p.id === id);
    setLine(key, {
      package: id,
      ...(pack ? { label: localized(pack.name, i18n.language), unit: pack.total_xaf ? String(pack.total_xaf) : "" } : {}),
    });
  };

  const submit = (asDraft: boolean) => (event?: FormEvent) => {
    event?.preventDefault();
    const filled = lines.filter((line) => line.label.trim() || line.unit);
    if (!contact) return setLocalError(t("billing.new.contactRequired"));
    if (!filled.length || filled.some((line) => !line.label.trim() || !(amountOf(line.unit) > 0) || !(amountOf(line.quantity) > 0))) {
      return setLocalError(t("billing.new.linesRequired"));
    }
    if (withSchedule && schedule.length && scheduled !== totals.ttc) {
      return setLocalError(t("billing.form.scheduleMismatch", { sum: formatXaf(scheduled), total: formatXaf(totals.ttc) }));
    }
    setLocalError("");
    const data = {
      kind,
      contact,
      lines: filled.map((line) => ({
        label: line.label.trim(),
        quantity: amountOf(line.quantity),
        unit_price_xaf: amountOf(line.unit),
        ...(line.package ? { package: line.package } : {}),
      })),
      ...(language ? { language } : {}),
      discount_xaf: amountOf(discount) || 0,
      due_date: due || null,
      notes: notes.trim(),
      installments: withSchedule ? schedule.filter((r) => r.due && amountOf(r.amount) > 0).map((r) => ({ due_date: r.due, amount_xaf: amountOf(r.amount) })) : [],
    };
    const done = (id: string) => {
      refresh();
      onClose();
      navigate(`/invoices/${id}`);
    };
    if (draft) {
      update.mutate({ id: draft.id, data }, { onSuccess: (response) => response.status === 200 && done(response.data.id) });
    } else {
      create.mutate({ data: { ...data, draft: asDraft } }, { onSuccess: (response) => response.status === 201 && done(response.data.id) });
    }
  };

  const contactOptions = [
    { value: "", label: contacts.isPending && !fixed ? t("common.loading") : t("billing.new.chooseContact") },
    ...found.map((c) => ({ value: c.id, label: c.phone_e164 ? `${c.full_name} · ${c.phone_e164}` : c.full_name })),
  ];
  const packageOptions = [
    { value: "", label: t("billing.form.freeLine") },
    ...published.map((p) => ({
      value: p.id,
      label: `${p.code} — ${localized(p.name, i18n.language)}${p.total_xaf != null ? ` · ${formatXaf(p.total_xaf)}` : ""}`,
    })),
  ];

  return (
    <GlassSheet open={open} title={draft ? t("billing.form.editTitle") : t("billing.new.title")} onClose={onClose}>
      <form className="stack" onSubmit={submit(false)} noValidate>
        <div className="form-grid">
          <SelectField
            label={t("billing.new.kind")}
            value={kind}
            options={(["invoice", "quote"] as const).map((k) => ({ value: k, label: t(`billing.kind.${k}`) }))}
            onChange={(e) => setKind(e.target.value as ManualInvoiceKindEnum)}
          />
          <SelectField
            label={t("billing.new.language")}
            value={language}
            options={[
              { value: "", label: t("billing.new.clientLanguage") },
              { value: "fr", label: t("billing.new.fr") },
              { value: "en", label: t("billing.new.en") },
            ]}
            onChange={(e) => setLanguage(e.target.value as "" | "fr" | "en")}
          />
        </div>
        {fixed ? (
          <p>
            {t("billing.new.contact")} : <strong>{contactName ?? draft?.client_name}</strong>
          </p>
        ) : (
          <>
            <TextField label={t("billing.new.searchContact")} hint={t("billing.new.searchHint")} type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
            <SelectField label={t("billing.new.contact")} value={contact} error={errors.contact} options={contactOptions} onChange={(e) => setContact(e.target.value)} />
          </>
        )}
        <h3>{t("billing.new.lines")}</h3>
        {lines.map((line, index) => (
          <fieldset key={line.key} className="invoice-line invoice-line--full">
            <legend className="visually-hidden">{t("billing.form.lineN", { n: index + 1 })}</legend>
            <SelectField label={t("billing.form.fromCatalog")} value={line.package} options={packageOptions} onChange={(e) => pickPackage(line.key, e.target.value)} />
            <TextField label={t("billing.new.label", { n: index + 1 })} value={line.label} maxLength={200} onChange={(e) => setLine(line.key, { label: e.target.value })} />
            <div className="invoice-line__numbers">
              <TextField
                label={t("billing.form.quantity")}
                inputMode="numeric"
                value={line.quantity}
                onChange={(e) => setLine(line.key, { quantity: e.target.value.replace(/\D/g, "") })}
              />
              <TextField
                label={t("billing.form.unitPrice")}
                inputMode="numeric"
                value={line.unit ? formatXaf(amountOf(line.unit), { suffix: false }) : ""}
                onChange={(e) => setLine(line.key, { unit: e.target.value.replace(/\D/g, "") })}
              />
              <p className="invoice-line__amount ao-num" aria-live="polite">
                {formatXaf((amountOf(line.quantity) || 0) * (amountOf(line.unit) || 0))}
              </p>
              {lines.length > 1 ? (
                <Button variant="ghost" aria-label={t("billing.new.removeLine", { n: index + 1 })} onClick={() => setLines((all) => all.filter((l) => l.key !== line.key))}>
                  <Trash2 size={16} aria-hidden="true" />
                </Button>
              ) : null}
            </div>
          </fieldset>
        ))}
        <div className="row">
          <Button variant="secondary" icon={<Plus size={16} aria-hidden="true" />} onClick={() => setLines((all) => [...all, newLine()])}>
            {t("billing.new.addLine")}
          </Button>
        </div>
        <div className="form-grid">
          <TextField
            label={t("billing.form.discount")}
            inputMode="numeric"
            value={discount ? formatXaf(amountOf(discount), { suffix: false }) : ""}
            error={errors.discount_xaf}
            onChange={(e) => setDiscount(e.target.value.replace(/\D/g, ""))}
          />
          {kind === "invoice" ? (
            <TextField label={t("billing.form.due")} type="date" hint={t("billing.form.dueHint")} min={todayInDouala()} value={due} error={errors.due_date} onChange={(e) => setDue(e.target.value)} />
          ) : null}
        </div>
        <dl className="totals" aria-label={t("billing.form.preview")}>
          {totals.discount ? (
            <>
              <dt>{t("billing.form.discount")}</dt>
              <dd>− {formatXaf(totals.discount)}</dd>
            </>
          ) : null}
          <dt>{t("billing.detail.ht")}</dt>
          <dd>{formatXaf(totals.ht)}</dd>
          <dt>{t("billing.form.vat")}</dt>
          <dd>{formatXaf(totals.vat)}</dd>
          <dt className="totals__strong">{t("billing.detail.ttc")}</dt>
          <dd className="totals__strong">{formatXaf(totals.ttc)}</dd>
        </dl>
        <p className="muted">{t("billing.new.vatHint")}</p>
        {kind === "invoice" ? (
          <>
            <SoftToggle label={t("billing.form.schedule")} checked={withSchedule} onChange={setWithSchedule} />
            {withSchedule ? (
              <div className="stack">
                <div className="row">
                  {[2, 3, 4].map((count) => (
                    <Button
                      key={count}
                      variant="secondary"
                      onClick={() =>
                        setSchedule(splitInstalments(totals.ttc, count).map((amount) => ({ key: nextKey++, due: "", amount: String(amount) })))
                      }
                    >
                      {t("billing.form.splitIn", { count })}
                    </Button>
                  ))}
                </div>
                {schedule.map((row, index) => (
                  <div key={row.key} className="invoice-line__numbers">
                    <TextField
                      label={t("billing.form.instalmentDue", { n: index + 1 })}
                      type="date"
                      value={row.due}
                      onChange={(e) => setSchedule((all) => all.map((r) => (r.key === row.key ? { ...r, due: e.target.value } : r)))}
                    />
                    <TextField
                      label={t("billing.form.instalmentAmount")}
                      inputMode="numeric"
                      value={row.amount ? formatXaf(amountOf(row.amount), { suffix: false }) : ""}
                      onChange={(e) => setSchedule((all) => all.map((r) => (r.key === row.key ? { ...r, amount: e.target.value.replace(/\D/g, "") } : r)))}
                    />
                    <Button variant="ghost" aria-label={t("billing.form.removeInstalment", { n: index + 1 })} onClick={() => setSchedule((all) => all.filter((r) => r.key !== row.key))}>
                      <Trash2 size={16} aria-hidden="true" />
                    </Button>
                  </div>
                ))}
                <div className="row between">
                  <Button variant="secondary" icon={<Plus size={16} aria-hidden="true" />} onClick={() => setSchedule((all) => [...all, { key: nextKey++, due: "", amount: "" }])}>
                    {t("billing.form.addInstalment")}
                  </Button>
                  <span className={scheduled === totals.ttc ? "ok" : "muted"}>{t("billing.form.scheduled", { sum: formatXaf(scheduled), total: formatXaf(totals.ttc) })}</span>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
        <TextAreaField label={t("billing.form.notes")} hint={t("billing.form.notesHint")} rows={2} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} />
        {localError || errors.lines || errors.installments || errors.detail || (mutation.isError && !Object.keys(errors).length) ? (
          <p role="alert" className="ao-field__error">
            {localError || errors.lines || errors.installments || errors.detail || errorText(mutation.error, t("common.error"))}
          </p>
        ) : null}
        <div className="row">
          {draft ? (
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? t("common.loading") : t("billing.form.saveDraft")}
            </Button>
          ) : (
            <>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? t("common.loading") : kind === "quote" ? t("billing.new.createQuote") : t("billing.new.createInvoice")}
              </Button>
              <Button variant="secondary" disabled={mutation.isPending} onClick={() => submit(true)()}>
                {t("billing.form.saveDraft")}
              </Button>
            </>
          )}
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </GlassSheet>
  );
}
